/**
 * Where each navigation lands on the page.
 *
 * **A fresh load starts at the top** (2026-10-14: refreshing the home page used to land on the
 * footer). On a refresh, Vue Router hands back the position saved as the page unloaded, and this
 * used to return it as-is. The restore runs as soon as the route renders. By then the page's
 * lazily loaded sections (neighbourhoods, journey, FAQ, CTA) do not exist yet, so the page is
 * only about half its height and the browser clamps the position to the bottom: the footer.
 * Each section then arrives above the footer, and Chrome's scroll anchoring keeps the footer in
 * view, carrying the page down to the very bottom (measured: 2641 → 4396 → 6514px).
 * So a fresh load (a refresh, or an opened link) starts at the top, or at its `#anchor`.
 *
 * **Back and forward keep their place, without racing the page.** A saved position is restored
 * only once the page is tall enough to hold it, or after a short wait at most.
 */
import { type RouteLocationNormalized, type RouterScrollBehavior, START_LOCATION } from 'vue-router'

type Position = { left: number; top: number }

/** The longest a back or forward navigation waits for its page to grow tall enough. */
export const RESTORE_WAIT_MS = 1500

/** Resolves with `saved` once the page can scroll that far, or after `RESTORE_WAIT_MS` regardless. */
export function whenPageHolds(saved: Position, wait = RESTORE_WAIT_MS): Promise<Position> {
  return new Promise((resolve) => {
    const started = performance.now()
    const check = () => {
      const room = document.documentElement.scrollHeight - window.innerHeight
      if (room >= saved.top || performance.now() - started >= wait) resolve(saved)
      else requestAnimationFrame(check)
    }
    check()
  })
}

export const scrollBehavior: RouterScrollBehavior = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  saved,
) => {
  const freshLoad = from === START_LOCATION
  if (saved && !freshLoad) return whenPageHolds({ left: saved.left ?? 0, top: saved.top ?? 0 })
  if (to.hash && to.meta.layout === 'marketplace') return { el: to.hash, top: 100 }
  if (to.path === from.path && to.path === '/listings' && to.query.page === from.query.page)
    return false
  return { top: 0 }
}
